import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, openDownload } from "@/lib/api";
import {
  Activity as ActivityIcon, Filter, User as UserIcon, Clock, ArrowRight,
  Plus, Pencil, Trash2, RefreshCw, ChevronLeft, ChevronRight, Download, ExternalLink,
} from "lucide-react";

const METHOD_STYLE = {
  POST:   { bg: "bg-emerald-100 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", icon: Plus,   label: "OLUŞTUR" },
  PUT:    { bg: "bg-blue-100 dark:bg-blue-950/40",       text: "text-blue-700 dark:text-blue-300",       icon: Pencil, label: "GÜNCELLE" },
  PATCH:  { bg: "bg-blue-100 dark:bg-blue-950/40",       text: "text-blue-700 dark:text-blue-300",       icon: Pencil, label: "DÜZELT" },
  DELETE: { bg: "bg-rose-100 dark:bg-rose-950/40",       text: "text-rose-700 dark:text-rose-300",       icon: Trash2, label: "SİL" },
};

const PAGE = 50;

// Entity → route mapping for detail navigation
const ENTITY_ROUTE = {
  "Kasa": "/kasa",
  "Kasa Hareketi": "/kasa",
  "Kasa Sayım": "/kasa",
  "Kasa Kategori": "/kasa",
  "Gelir/Gider": "/gelirler",
  "Banka Hesabı": "/banka",
  "Cari": "/cari",
  "Çek": "/cek",
  "Kredi": "/kredi",
  "Ödeme/Tahsilat": "/odemeler",
  "Ürün": "/urunler",
  "Proforma": "/proforma",
  "Ajanda": "/ajanda",
  "Tekrarlayan": "/tekrarlayan",
  "Şirket Bilgileri": "/ayarlar",
};

export default function Activity() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [entity, setEntity] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entities, setEntities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip };
      if (entity) params.entity = entity;
      if (userEmail) params.user_email = userEmail;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/activity", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  const loadMeta = async () => {
    try {
      const ent = await api.get("/activity/entities");
      setEntities(ent.data);
    } catch (err) { console.error("Entity listesi yüklenemedi", err); }
    // Users list requires owner role — silently skip for non-owners
    try {
      const us = await api.get("/auth/users");
      setUsers(us.data);
    } catch { setUsers([]); }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, entity, userEmail, fromDate, toDate]);

  const stats = useMemo(() => {
    const uniqueUsers = new Set(items.map(i => i.user_email)).size;
    return { uniqueUsers, count: items.length };
  }, [items]);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return iso; }
  };

  const clearFilters = () => {
    setEntity(""); setUserEmail(""); setFromDate(""); setToDate(""); setSkip(0);
  };

  const exportCsv = () => {
    const query = {};
    if (userEmail) query.user_email = userEmail;
    if (entity) query.entity = entity;
    if (fromDate) query.from_date = fromDate;
    if (toDate) query.to_date = toDate;
    openDownload("/api/activity/export.csv", query);
  };

  const openDetail = (it) => {
    const route = ENTITY_ROUTE[it.entity];
    if (route) nav(route);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Denetim İzi</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Aktivite Log'u</h1>
          <p className="text-sm text-slate-500 mt-1">Kim, ne zaman, hangi kaydı oluşturdu / güncelledi / sildi</p>
        </div>
        <button onClick={load} data-testid="refresh-activity"
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-bold hover:border-[#1E2A9C]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Yenile
        </button>
        <button onClick={exportCsv} data-testid="export-csv"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700">
          <Download className="w-3.5 h-3.5" /> CSV İndir
        </button>
      </div>

      {/* Filters */}
      <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-500 uppercase tracking-widest">
          <Filter className="w-3.5 h-3.5" /> Filtreler
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Kullanıcı</label>
            <select value={userEmail} onChange={(e) => { setUserEmail(e.target.value); setSkip(0); }}
              data-testid="filter-user"
              className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-100">
              <option value="">Tümü</option>
              {users.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Kayıt Tipi</label>
            <select value={entity} onChange={(e) => { setEntity(e.target.value); setSkip(0); }}
              data-testid="filter-entity"
              className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-100">
              <option value="">Tümü</option>
              {entities.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Baş. Tarih</label>
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setSkip(0); }}
              data-testid="filter-from"
              className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-100 font-mono-num" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Bit. Tarih</label>
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setSkip(0); }}
              data-testid="filter-to"
              className="w-full mt-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-100 font-mono-num" />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} data-testid="clear-filters"
              className="w-full px-3 py-2 rounded-lg text-sm font-bold text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:border-rose-400 hover:text-rose-600">
              Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">
          <span className="font-mono-num font-bold text-slate-900 dark:text-slate-100">{total}</span> kayıt bulundu ·
          gösterilen <span className="font-mono-num">{skip + 1}–{Math.min(skip + PAGE, total)}</span>
        </div>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-500 font-bold tracking-widest">
            <tr>
              <th className="px-4 py-3 text-left">Zaman</th>
              <th className="px-4 py-3 text-left">Kullanıcı</th>
              <th className="px-4 py-3 text-left">Aksiyon</th>
              <th className="px-4 py-3 text-left">Kayıt Tipi</th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">Yol</th>
              <th className="px-4 py-3 text-left hidden xl:table-cell">IP</th>
            </tr>
          </thead>
          <tbody data-testid="activity-list">
            {items.length === 0 && (
              <tr><td colSpan="6" className="text-center py-16 text-slate-400 text-sm">
                {loading ? "Yükleniyor..." : "Bu kriterlere uyan kayıt yok"}
              </td></tr>
            )}
            {items.map(it => {
              const M = METHOD_STYLE[it.method] || METHOD_STYLE.POST;
              const Icon = M.icon;
              const uname = users.find(u => u.email === it.user_email)?.name || it.user_email;
              const route = ENTITY_ROUTE[it.entity];
              const clickable = !!route;
              return (
                <tr key={it.id} data-testid={`row-${it.id}`}
                  onClick={clickable ? () => openDetail(it) : undefined}
                  className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${clickable ? "cursor-pointer" : ""}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-xs font-mono-num text-slate-700 dark:text-slate-300">
                      <Clock className="w-3 h-3 text-slate-400" />
                      {fmt(it.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#1E2A9C]/10 grid place-items-center">
                        <UserIcon className="w-3.5 h-3.5 text-[#1E2A9C]" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-200">{uname}</div>
                        <div className="text-[10px] text-slate-500 font-mono-num">{it.user_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${M.bg} ${M.text}`}>
                      <Icon className="w-3 h-3" /> {M.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{it.entity}</span>
                      {clickable && <ExternalLink className="w-3 h-3 text-slate-400" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <code className="text-[11px] text-slate-500 font-mono-num">{it.path}</code>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    <span className="text-[11px] text-slate-500 font-mono-num">{it.ip || "-"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setSkip(Math.max(0, skip - PAGE))} disabled={skip === 0}
            data-testid="page-prev"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-bold disabled:opacity-40 hover:border-[#1E2A9C]">
            <ChevronLeft className="w-3.5 h-3.5" /> Önceki
          </button>
          <span className="px-3 text-xs text-slate-500 font-mono-num">
            Sayfa {Math.floor(skip / PAGE) + 1} / {Math.ceil(total / PAGE)}
          </span>
          <button onClick={() => setSkip(skip + PAGE)} disabled={skip + PAGE >= total}
            data-testid="page-next"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-bold disabled:opacity-40 hover:border-[#1E2A9C]">
            Sonraki <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
